/* RestoreDetailRenderer.js */

"use strict";

const {
    Control: {
        Action: {Button},
        Boolean: {InputCheckbox},
        // Textfield: {InputText},
    },
} = require("@gluenetworks/steve_ui_core");
const {PopUp} = require("@gluenetworks/steve_ui_widgets");
const _ = require("lodash");

class RestoreDetailRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("base-restore-veil-gl");
        this._rootElem.component = this._model;

        this.nameContainer = document.createElement("div");
        this.nameContainer.classList.add("name-container");

        const nameLabel = document.createElement("label");
        nameLabel.innerText = "Name";

        this._devicesContainer = document.createElement("div");
        this._devicesContainer.classList.add("devices-container");

        this._devicesLabel = document.createElement("label");
        this._devicesLabel.innerHTML = "Devices:";

        this._unsupportedDevicesMsg = document.createElement("span");
        this._unsupportedDevicesMsg.classList.add("unsupported-devices-msg");

        this._runCaptureCheckbox = new InputCheckbox({
            label: "Capture new snapshot",
            labelShown: true,
            value: true,
            darkTheme: true,
        });

        this._setDefaultCaptureCheckbox = new InputCheckbox({
            label: "Set new snapshot as the default configuration",
            labelShown: true,
            value: false,
            darkTheme: true,
            disabled: false,
        });

        this._confirmButton = new Button({
            label: "Start Restore",
            darkTheme: true,
        });

        this._cancelButton = new Button({
            label: "Cancel",
            darkTheme: true,
        });

        this._scheduleButton = new Button({
            label: "Schedule Restore",
            darkTheme: true,
        });

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = this._model._title;

        this._messageBox = document.createElement("p");
        this._messageBox.innerText = `You are about to restore the backup '${this._model._backupEntity.getDescription()}' on the device '${this._model._device.getName()}'.`;

        this._buttonContainer = document.createElement("button-container");

        this._registerListeners();
    }

    _registerListeners() {
        this._confirmButton.on("UserClick", this._onConfirmButtonClick.bind(this));
        this._cancelButton.on("UserClick", this._onCancelButtonClick.bind(this));
        this._scheduleButton.on("UserClick", this._schedulePlanAction.bind(this));
        this._runCaptureCheckbox.on(
            "UserValue",
            (this._onRunCapture = this._onRunCapture.bind(this))
        );
    }

    _unregisterListeners() {
        this._confirmButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._scheduleButton.removeListener("UserClick", this._schedulePlanAction);
        this._runCaptureCheckbox.removeAllListeners("UserValue");
    }

    _onConfirmButtonClick() {
        this._callCreateAction();
    }

    _onCancelButtonClick() {
        this._model.cancelAction();
    }

    _callCreateAction() {
        this._model.restoreAction({
            postCapture: this._runCaptureCheckbox.getValue(),
            defaultSnapshot: this._setDefaultCaptureCheckbox.getValue(),
        });
    }

    _schedulePlanAction() {
        const workInput = this._getWorkInput();
        return this._model.scheduleRestore("Restore " + workInput.description, workInput);
    }

    _onRunCapture(val) {
        if (val) {
            this._setDefaultCaptureCheckbox.setDisabled(false);
        } else {
            this._setDefaultCaptureCheckbox.setDisabled(true);
            this._setDefaultCaptureCheckbox.setValue(false);
        }
    }

    _getWorkInput() {
        let workInput = {
            description: this._model._backupEntity.description,
            postCapture: this._runCaptureCheckbox.getValue(),
            defaultSnapshot: this._setDefaultCaptureCheckbox.getValue(),
            restoreCommands: this._model._device.restoreCommands || [],
            deviceBackup: {},
        };
        const deviceId = this._model._device.id;
        const backupId = this._model._backupEntity.getId();

        if (deviceId && backupId) {
            workInput.deviceBackup[deviceId] = backupId;
        } else {
            this._model.logger.warn(
                {},
                "RestoreDetailRenderer._getWorkInput() error: cannot set deviceBackup"
            );
        }

        return workInput;
    }

    shutdown() {
        this._unregisterListeners();

        const promArr = [
            this._runCaptureCheckbox.shutdown(),
            this._confirmButton.shutdown(),
            this._cancelButton.shutdown(),
            this._setDefaultCaptureCheckbox.shutdown(),
        ];

        return Promise.all(promArr)
            .then(() => {
                this._runCaptureCheckbox =
                    this._confirmButton =
                    this._buttonContainer =
                    this._titleComponent =
                    this._messageBox =
                    this._model =
                    this._rootElem =
                    this._cancelButton =
                        undefined;
            })
            .catch((err) => {
                this._model.logger.warn(err, "RestoreDetailRenderer.shutdown() error");
            });
    }

    render() {
        return this.model.getSupportedOs().then(() => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titleComponent);
                this._rootElem.appendChild(this._messageBox);
                this._rootElem.appendChild(this._runCaptureCheckbox.renderer.rootElement);
                this._rootElem.appendChild(this._setDefaultCaptureCheckbox.renderer.rootElement);

                this._buttonContainer.appendChild(this._unsupportedDevicesMsg);
                this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._scheduleButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._confirmButton.renderer.rootElement);
                this._rootElem.appendChild(this._buttonContainer);
            }

            return Promise.resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    _createList(array) {
        const uniqueSet = new Set();
        array.forEach((item) => uniqueSet.add(item));

        const ul = document.createElement("ul");

        // Loop through the array and create list items
        uniqueSet.forEach(function (item) {
            // Create a list item element
            const li = document.createElement("li");

            // Set the text content of the list item to the array value
            li.textContent = item || "Empty OS";
            ul.appendChild(li);
        });
        return ul;
    }

    _createPopUp(toolTipContent, anchor) {
        let viewMoreContentElement = document.createElement("view-more-pop-up");
        viewMoreContentElement.classList.add("supported-os-popup");
        viewMoreContentElement.innerHTML = toolTipContent.outerHTML;
        this._popUpWidget = new PopUp({
            content: viewMoreContentElement.outerHTML,
            anchor: anchor,
            anchorPoint: "right",
            displayDirection: "bottomRight",
            openOnHover: true,
            parent: viewMoreContentElement.parentNode,
        });
    }

    _showHideUnsupportedDevices() {
        const currentDetectedOs = _.get(
                this._model._gameEditor,
                "_selectedDevice.detectedOs",
                "Empty OS"
            ),
            _unsupportedDevice = this._model._supportedOsList.indexOf(currentDetectedOs) === -1;

        this._viewWarningIcon = document.createElement("actionable-icon-gl");
        this._viewWarningIcon.dataset.icon = "common.warning";
        const msgStart = `Device running an`;
        this._unsupportedDevicesMsg.innerHTML = `${this._viewWarningIcon.outerHTML}${msgStart}`;
        const msgUnsupportedOS = document.createElement("span");
        msgUnsupportedOS.innerHTML = "<u>unsupported OS</u>";
        msgUnsupportedOS.classList.add("os-name-anchor");

        this._unsupportedDevicesMsg.appendChild(msgUnsupportedOS);
        const listOfSUpportedOs = document.createElement("span");
        listOfSUpportedOs.innerHTML = currentDetectedOs;
        this._createPopUp(listOfSUpportedOs, msgUnsupportedOS);

        if (_unsupportedDevice) {
            this._unsupportedDevicesMsg.classList.remove("hidden");
        } else {
            this._unsupportedDevicesMsg.classList.add("hidden");
        }
    }
}

module.exports = RestoreDetailRenderer;
